import { AlertTriangle, CheckCircle2, Key, Shield, Users } from "lucide-react";
import React from "react";
import { TODAY } from "@/data/financial-systems";

const ROLES = [
  {
    name: "Chief Financial Officer",
    userCount: 1,
    permissionCount: 34,
    lastModified: "12 Mar 2026",
    modifiedBy: "David Chen",
    riskLevel: "low" as const,
    highlights: [
      "Full financial visibility",
      "Multi-entity access",
      "Approve scenarios",
    ],
  },
  {
    name: "Finance Manager",
    userCount: 2,
    permissionCount: 28,
    lastModified: "14 Mar 2026",
    modifiedBy: "David Chen",
    riskLevel: "low" as const,
    highlights: [
      "Edit recurring commitments",
      "Manage scenarios",
      "View all forecasts",
    ],
  },
  {
    name: "Accounts Assistant",
    userCount: 3,
    permissionCount: 14,
    lastModified: "01 Feb 2026",
    modifiedBy: "Sarah Mitchell",
    riskLevel: "low" as const,
    highlights: ["View transactions", "Categorise items", "View dashboard"],
  },
  {
    name: "CEO",
    userCount: 1,
    permissionCount: 12,
    lastModified: "18 Jan 2026",
    modifiedBy: "David Chen",
    riskLevel: "low" as const,
    highlights: ["Executive dashboard", "Strategic insights", "View reports"],
  },
  {
    name: "External User",
    userCount: 1,
    permissionCount: 6,
    lastModified: "22 Jan 2026",
    modifiedBy: "David Chen",
    riskLevel: "low" as const,
    highlights: ["Board-level summary", "Critical alerts", "View scenarios"],
  },
  {
    name: "System Administrator",
    userCount: 1,
    permissionCount: 22,
    lastModified: "10 Mar 2026",
    modifiedBy: "David Chen",
    riskLevel: "medium" as const,
    highlights: ["Full admin access", "User management", "Role configuration"],
  },
];

const RECENT_CHANGES = [
  {
    date: "14 Mar 2026",
    user: "David Chen",
    action: "Permission added",
    detail: "Finance Manager → manage_recurring_transactions",
    type: "add" as const,
  },
  {
    date: "12 Mar 2026",
    user: "David Chen",
    action: "Role modified",
    detail: "CFO role — strategic KPIs card enabled",
    type: "modify" as const,
  },
  {
    date: "10 Mar 2026",
    user: "David Chen",
    action: "User assigned",
    detail: "Emma Thompson → Accounts Assistant role",
    type: "add" as const,
  },
  {
    date: "01 Feb 2026",
    user: "Sarah Mitchell",
    action: "Permission removed",
    detail:
      "Accounts Assistant → manage_scenarios (not required at this level)",
    type: "remove" as const,
  },
  {
    date: "18 Jan 2026",
    user: "David Chen",
    action: "Role created",
    detail: "External User role created for board member access",
    type: "add" as const,
  },
];

const RISK_FLAGS = [
  {
    level: "info" as const,
    message:
      "System Administrator role has full admin access — assigned to 1 user only. Recommend periodic review.",
  },
  {
    level: "ok" as const,
    message:
      "All roles have at least one active user. No orphaned roles detected.",
  },
  {
    level: "ok" as const,
    message:
      "Lockout protection active — manage_roles_permissions cannot be removed from all users.",
  },
];

export const RolesPermissionsAuditPreview: React.FC = () => {
  const reportDate = TODAY.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const totalUsers = ROLES.reduce((s, r) => s + r.userCount, 0);
  const totalPermissions = ROLES.reduce((s, r) => s + r.permissionCount, 0);

  return (
    <div className="space-y-6">
      {/* Header strip */}
      <div className="rounded-lg border bg-primary/5 border-primary/20 px-6 py-4 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-lg">Roles &amp; Permissions Audit</h3>
          <p className="text-sm text-muted-foreground">
            BuildRight Construction Ltd · Full permissions audit
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">Report Date</p>
          <p className="font-semibold">{reportDate}</p>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-lg border bg-muted/40 p-4 text-center">
          <Users className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
          <p className="text-2xl font-bold">{totalUsers}</p>
          <p className="text-xs text-muted-foreground mt-1">Active Users</p>
        </div>
        <div className="rounded-lg border bg-muted/40 p-4 text-center">
          <Shield className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
          <p className="text-2xl font-bold">{ROLES.length}</p>
          <p className="text-xs text-muted-foreground mt-1">Defined Roles</p>
        </div>
        <div className="rounded-lg border bg-muted/40 p-4 text-center">
          <Key className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
          <p className="text-2xl font-bold">{totalPermissions}</p>
          <p className="text-xs text-muted-foreground mt-1">
            Total Permission Assignments
          </p>
        </div>
      </div>

      {/* Roles table */}
      <div>
        <p className="text-sm font-semibold mb-3">Role Summary</p>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/60">
                <th className="px-4 py-3 text-left font-semibold">Role</th>
                <th className="px-4 py-3 text-right font-semibold">Users</th>
                <th className="px-4 py-3 text-right font-semibold">
                  Permissions
                </th>
                <th className="px-4 py-3 text-left font-semibold">
                  Key Capabilities
                </th>
                <th className="px-4 py-3 text-left font-semibold">
                  Last Modified
                </th>
                <th className="px-4 py-3 text-left font-semibold">Risk</th>
              </tr>
            </thead>
            <tbody>
              {ROLES.map((role, i) => (
                <tr
                  key={i}
                  className="border-b hover:bg-muted/30 transition-colors"
                >
                  <td className="px-4 py-3 font-medium">{role.name}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {role.userCount}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {role.permissionCount}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {role.highlights.map((h) => (
                        <span
                          key={h}
                          className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                        >
                          {h}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    <span className="block">{role.lastModified}</span>
                    <span className="text-xs">by {role.modifiedBy}</span>
                  </td>
                  <td className="px-4 py-3">
                    {role.riskLevel === "low" ? (
                      <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400 text-xs font-medium">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        Low
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400 text-xs font-medium">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Review
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent changes */}
      <div>
        <p className="text-sm font-semibold mb-3">
          Recent Permission Changes (Last 90 Days)
        </p>
        <div className="space-y-2">
          {RECENT_CHANGES.map((change, i) => (
            <div
              key={i}
              className="flex items-start gap-3 rounded-lg border bg-muted/20 px-4 py-3"
            >
              <div
                className={`mt-0.5 h-2 w-2 rounded-full flex-shrink-0 ${change.type === "add" ? "bg-green-500" : change.type === "remove" ? "bg-red-500" : "bg-amber-500"}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{change.action}</span>
                  <span className="text-xs text-muted-foreground">
                    · {change.date} · {change.user}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {change.detail}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Risk flags */}
      <div>
        <p className="text-sm font-semibold mb-3">Audit Findings</p>
        <div className="space-y-2">
          {RISK_FLAGS.map((flag, i) => (
            <div
              key={i}
              className={`rounded-lg border px-4 py-3 flex gap-3 ${flag.level === "ok" ? "border-green-200 bg-green-50/60 dark:bg-green-950/20 dark:border-green-800" : "border-blue-200 bg-blue-50/60 dark:bg-blue-950/20 dark:border-blue-800"}`}
            >
              {flag.level === "ok" ? (
                <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
              )}
              <p className="text-sm">{flag.message}</p>
            </div>
          ))}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Generated by Access Cash Forecaster · {reportDate} · For internal audit
        use only · Exported by David Chen (System Administrator)
      </p>
    </div>
  );
};
